"use client";

import React, { useEffect, useState } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Loader2, Copy, Trash2, Info, AlertCircle, Eye, EyeOff } from "lucide-react";
import { createApiKey, deleteApiKey, getApiKey } from "core";
import { toast } from "components/ui/toast";
import ConfirmationDialog from "components/ui/confirmationDialog";
import PageContainer from "components/ui/page-container";
import DeveloperHeader from "./DeveloperHeader";

export default function DeveloperSettings() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showKey, setShowKey] = useState(false);

  /* ---------------- Load existing API key ---------------- */
  useEffect(() => {
    const fetchKey = async () => {
      setLoading(true);
      try {
        const res: any = await getApiKey();
        if (res?.data?.apiKey) {
          setApiKey(res.data.apiKey);
        } else {
          setApiKey(null);
        }
      } catch {
        toast.error("Failed to fetch API key");
      } finally {
        setLoading(false);
      }
    };
    fetchKey();
  }, []);

  /* ---------------- Generate API key ---------------- */
  const handleCreate = async () => {
    setLoading(true);
    try {
      const res: any = await createApiKey();
      if (res?.data?.apiKey) {
        setApiKey(res.data.apiKey);
        toast.success("API key generated!");
      } else {
        toast.error("Failed to generate API key!");
      }
    } catch {
      toast.error("Failed to generate API key!");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Delete API key ---------------- */
  const handleDelete = async () => {
    setShowConfirm(false);
    setLoading(true);
    try {
      await deleteApiKey();
      setApiKey(null);
      toast.success("API key deleted!");
    } catch {
      toast.error("Failed to delete API key!");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Copy key ---------------- */
  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy key!");
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <PageContainer>
      {/* Header Component */}
      <DeveloperHeader />

      {/* Info Alert */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-5 mb-6 flex items-start gap-4 shadow-lg">
        <div className="p-2 /20 rounded-lg flex-shrink-0">
          <Info className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="text-base font-semibold text-white mb-2">API Key Information</h4>
          <p className="text-sm text-blue-50 leading-relaxed">
            Generate an API key to integrate our services with your application. Keep your key secure and never share it publicly.
          </p>
        </div>
      </div>

      {/* API Key Management Card */}
      <div className="bg-surface dark:bg-gray-800 rounded-xl shadow-sm border border-line dark:border-gray-700 p-6">
        <div className="space-y-6">
          {/* Card Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-content dark:text-white">API Key</h3>
              <p className="text-sm text-content-secondary dark:text-gray-400 mt-1">
                {apiKey ? "Your active API key for authentication" : "Generate an API key to get started"}
              </p>
            </div>
            {apiKey && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-700 rounded-full">
                <div className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </div>
                <span className="text-xs font-semibold text-green-700 dark:text-green-400">Active</span>
              </div>
            )}
          </div>

          {/* API Key Input and Actions */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Developer Key
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Input
                    type={showKey ? "text" : "password"}
                    value={apiKey ?? ""}
                    placeholder={loading ? "Loading..." : "No API key generated"}
                    readOnly
                    className="font-mono text-sm pr-10 bg-gray-50 dark:bg-gray-900/50"
                  />
                  {apiKey && (
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary dark:hover:text-gray-300 transition-colors"
                      aria-label={showKey ? "Hide API key" : "Show API key"}
                    >
                      {showKey ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>

                <div className="flex gap-2">
                  {loading ? (
                    <Button
                      variant="secondary"
                      disabled
                      className="flex items-center gap-2 whitespace-nowrap min-w-[120px]"
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing
                    </Button>
                  ) : !apiKey ? (
                    <Button
                      onClick={handleCreate}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 whitespace-nowrap shadow-md hover:shadow-lg transition-all"
                    >
                      Generate Key
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleCopy}
                        className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border-line-strong dark:border-gray-600"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setShowConfirm(true)}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Security Warning */}
            {apiKey && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-amber-500 dark:border-amber-600 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
                    Security Notice
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                    Keep your API key confidential. If compromised, delete it immediately and generate a new one. Never commit keys to version control or share them publicly.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documentation Card */}
      <div className="bg-surface dark:bg-gray-800 rounded-xl border border-line dark:border-gray-700 p-6 mt-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-content dark:text-white mb-2">API Documentation</h3>
            <p className="text-sm text-content-secondary dark:text-gray-300 mb-4">
              Learn how to integrate our API into your application with our comprehensive documentation and code examples.
            </p>
            <Button
              variant="secondary"
              className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
              onClick={() => window.open('/api-docs', '_blank')}
            >
              View Documentation
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirm}
        title="Delete API Key"
        description="Are you sure you want to delete your API key? This action cannot be undone and will break any integrations using this key."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirm(false)}
      />
    </PageContainer>
  );
}
